'use client';

import classNames from 'classnames';
import { useTranslations } from 'next-intl';

import { RecapAsset } from '@/api/trading/types';
import { useRecap } from '@/api/trading/useRecap';
import { calcSpread } from '@/api/utils';
import { AssetIcon } from '@/blocks/components/AssetIcon';
import { Container } from '@/blocks/components/Container';
import { IconPriceTrend } from '@/blocks/components/Table/IconPriceTrend/IconPriceTrend';
import { WithTrendColor } from '@/blocks/components/WithTrendColor';
import { FormatAssetName, FormatPercent, FormatPrice } from '@/blocks/formatters';
import { extractAssetName } from '@/blocks/formatters/format-asset-name';
import { BackButton } from '@/blocks/sections/Hero/BackButton';
import buttons from '@/styles/button.module.scss';
import typography from '@/styles/typography.module.scss';

import { useSignupForAssetModal } from '@/app/[lang]/price-chart/[category]/[symbol]/components/WithSignupForAsset/WithSignupForAsset';
import NonTradableAssetBannerIcon from '@/app/[lang]/price-chart/[category]/[symbol]/sections/CoinPageHeroSection/NonTradableIcon';
import { BuySellButtons } from '@/app/[lang]/price-chart/[category]/[symbol]/sections/CoinPageMainSection/BuySellWidget';
import { getHubLink } from '@/app/[lang]/price-chart/[category]/navigation-utils';
import { Asset as LocalAsset } from '@/app/[lang]/price-chart/[category]/types';

import styles from './styles.module.scss';

function NotTradedBanner() {
  const { openSignupForAssetDialog } = useSignupForAssetModal();
  const tr = useTranslations();

  return (
    <div className={styles.notTradedBanner}>
      <div className={styles.notTradedBannerInner}>
        <NonTradableAssetBannerIcon />
        <p className={classNames(typography.h6, styles.notTradedBannerTitle)}>
          {tr.rich('pages.priceCharts.asset.sections.hero.nonTradeable.title')}
        </p>
        <div className={classNames(typography.bodyS, styles.notTradedBannerDescription)}>
          {tr('pages.priceCharts.asset.sections.hero.nonTradeable.description')}
        </div>
        <button
          onClick={openSignupForAssetDialog}
          className={classNames(buttons.btn, buttons.btnS, buttons.btnPrimary)}
        >
          {tr('pages.priceCharts.asset.sections.hero.nonTradeable.cta')}
        </button>
      </div>
    </div>
  );
}

export function CoinPageHeroSection({
  asset,
  initialData,
  assetTradable,
}: {
  assetTradable: boolean;
  asset: LocalAsset;
  initialData: RecapAsset[];
}) {
  const { markets } = useRecap(undefined, initialData);

  if (!markets) {
    return null;
  }

  const assetToRender: RecapAsset = {
    name: asset.name,
    description: asset.description,
    base: asset.name.split('/')[0],
    quote: asset.name.split('/')[1] || 'USD',
    ...markets.find((a) => a.name === asset.name),
  } as RecapAsset;

  function getRate(asset: RecapAsset) {
    const open = asset.price - (asset.price / (100 + asset.change24h)) * asset.change24h;
    return asset.price - open;
  }

  return (
    <section className={styles.hero}>
      <Container>
        <BackButton href={getHubLink(asset.category)} />
        <div className={styles.heroInner}>
          <div className={styles.heroCoinSummary}>
            <AssetIcon assetName={asset.name} className={styles.icon} />
            <div>
              <h1 className={classNames(typography.h4, styles.assetName)}>
                <FormatAssetName assetName={extractAssetName(asset)} />
              </h1>
              {/*<p className={typography.subheadingML}>{assetToRender.description}</p>*/}
              {/* TODO hours */}
              {/*{assetTradable ? (*/}
              {/*  <div className={styles.workingHours}>*/}
              {/*    <WithTrendColor className={classNames(typography.bodyM)} negative={false}>*/}
              {/*      <span className={classNames(typography.bodyM, styles.statusLabel)}>• TODO</span>*/}
              {/*    </WithTrendColor>*/}
              {/*    <p className={typography.bodyM}>(Closes in [TIME TO CLOSE])</p>*/}
              {/*  </div>*/}
              {/*) : null}*/}
            </div>
          </div>
          {assetTradable ? (
            <div className={styles.heroCoinMarketData}>
              <p className={classNames(typography.h4, styles.price)}>
                <FormatPrice price={assetToRender.price} priceScale={assetToRender.priceScale} />
              </p>
              <div className={styles.change}>
                <IconPriceTrend negative={assetToRender.change24h < 0} filled />
                <WithTrendColor className={typography.bodyM} negative={assetToRender.change24h < 0}>
                  <FormatPercent value={getRate(assetToRender)} forcePlusSign={true} suffix={false} />
                </WithTrendColor>
                <WithTrendColor className={typography.bodyM} negative={assetToRender.change24h < 0}>
                  <FormatPercent value={assetToRender.change24h} forcePlusSign />
                </WithTrendColor>
              </div>
            </div>
          ) : null}
        </div>
        {!assetTradable ? <NotTradedBanner /> : null}
        {assetTradable && asset.category !== 'crypto' ? (
          <BuySellButtons className={styles.buySellButtons}>
            {calcSpread(assetToRender.ask, assetToRender.bid)}
          </BuySellButtons>
        ) : null}
      </Container>
    </section>
  );
}
